<template>
	<div
		class="form-group flex"
		:class="{ 'flex-col': type !== 'checkbox', 'items-center flex-row-reverse': type === 'checkbox' }"
		@click="$emit('click', $event)"
		:title="title"
	>
		<div class="w-full flex justify-between" v-if="label" :class="{ 'cursor-pointer': isToggleable }" @click="toggleInput">
			<label :class="labelClass">
				{{ label }} <span v-if="sublabel" class="text-gray-500 flex-shrink-0 break-words leading-none text-sm">{{ sublabel }}</span>
				<span v-if="!isDisabled && required" class="text-red-600 font-bold"> *</span>
			</label>
			<div v-if="isToggleable">
				<Toggle :active="!isDisabled" />
			</div>
		</div>
		<slot :disabled="isDisabled">
			<the-mask
				v-if="mask"
				@blur.native="blur"
				:type="type"
				ref="BaseInput"
				class="shadow-md base-input"
				v-model="model"
				:placeholder="placeholder || label"
				:disabled="isDisabled"
				:mask="mask"
				@change="$emit('change', $event)"
				:tokens="hexTokens"
				:masked="masked"
				:readonly="readonly"
			/>

			<input
				v-else
				@blur="blur"
				:type="type"
				ref="BaseInput"
				class="base-input"
				@change="$emit('change', $event)"
				@keyup="$emit('keyup', $event)"
				:class="{ 'w-full': type !== 'checkbox' }"
				v-model="model"
				:placeholder="placeholder || label"
				:disabled="isDisabled"
				:maxlength="maxlength"
				:tabindex="tabIndex"
				:readonly="readonly"
			/>
		</slot>

		<div class="input-errors" v-if="!isValid && !isDisabled">
			{{ errorMessage || validationMessage || getDefaultErrorsMessage }}
		</div>
		<div v-if="$slots.bottom" class="text-sm">
			<slot name="bottom" />
		</div>
	</div>
</template>

<script>
import { TheMask } from 'vue-the-mask';
import { _regxp, concatArray, getYesNoOption } from '@/plugins/Utils';
import Toggle from '@/components/forms/Toggle';

export default {
	name: 'BaseInput',
	inheritAttrs: false,
	components: { Toggle, TheMask },
	props: {
		readonly: { default: false },
		isToggleable: { default: false },
		sublabel: {},
		minNumber: {},
		mask: {},
		label: {},
		errorMessage: {},
		validationtype: {},
		required: {},
		placeholder: {},
		disabled: {},
		maxlength: {},
		value: {},
		title: { type: String },
		type: {
			default: 'text',
			type: String,
		},
		masked: {
			default: true,
		},
		validation: null,
		tabIndex: {},
		labelClass: {},
		filterOnly: { default: false },
	},
	computed: {
		isDisabled() {
			return this.disabled || (this.isToggleable && this.isInputDisabled);
		},
		getDefaultErrorsMessage: function() {
			if (this.label && this.required) {
				return this.label + ' ' + this.$t('is_required');
			}
			return this.$t('field_not_validated').toString();
		},
		model: {
			get() {
				return this.value;
			},
			set(v) {
				if (!this.filterOnly) {
					this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
				}

				this.$emit('input', v);
			},
		},
	},
	mounted() {
		this.checkValidationType();
	},
	watch: {
		model: function(oldValue, newValue) {
			if (oldValue !== newValue && newValue && oldValue) {
				this.validate();
			}
		},
	},
	data() {
		return {
			isInputDisabled: false,
			validationTypeValidation: null,
			validationMessage: null,
			isValid: true,
			regxp: _regxp(),
			hexTokens: {
				1: {
					pattern: /[0-1]/,
				},
				3: {
					pattern: /[0-3]/,
				},
				N: {
					// NUMBER
					pattern: /[0-9]/,
				},
				T: {
					// TIME
					pattern: /[0-5]/,
				},
				A: {
					// ALPHA UPPERCASE
					pattern: /[A-Za-z]/,
					transform: v => v.toUpperCase(),
				},
				a: {
					// ALPHA LOWERCASE
					pattern: /[A-Za-z]/,
					transform: v => v.toLowerCase(),
				},
				B: {
					// ALPHANUMERIC
					pattern: /[A-Za-z0-9]/,
					transform: v => v.toUpperCase(),
				},
				E: {
					pattern: /[0-9Ee]/,
					transform: v => v.toUpperCase(),
				},
				M: {
					pattern: /[A-Za-zÄÜÖäüö0-9 ]/,
					transform: v => v.toUpperCase(),
				},
				L: {
					// LICENSE PLATE FIRST PART
					pattern: /[A-Za-zÄÜÖäüö]/,
					transform: v => v.toUpperCase(),
				},
				F: {
					// FAHRGESTELLNUMMER
					pattern: /(?![IQ])[A-Za-z0-9]/,
					transform: v => v.toUpperCase(),
				},
				C: {
					// Alle any (non)-whitespace to UpperCase
					pattern: /[\s\S]+/,
					transform: v => v.toUpperCase(),
				},
				U: {
					// URL-Safe-Chars
					pattern: /^[ ÄäÜüÖöa-zA-Z0-9_-]*$/,
				},
				X: {
					// Alle any (non)-whitespace
					pattern: /[\s\S]+/,
				},
				'!': { escape: true },
			},
		};
	},
	methods: {
		focus() {
			if (this.mask) {
				this.$nextTick(() => {
					this.$refs.BaseInput.$el.focus();
				});
			} else {
				this.$refs.BaseInput.focus();
			}
		},
		blur() {
			this.validate();
			this.$emit('blur');
		},
		toggleInput() {
			if (this.isToggleable) {
				this.model = null;
				this.isInputDisabled = !this.isInputDisabled;
			}
		},
		checkValidationType() {
			switch (this.validationtype) {
				case 'email':
					this.validationTypeValidation = this.regxp.email;
					this.validationMessage = this.$t('e_mail_invalid');
					break;
				case 'phone':
					this.validationTypeValidation = this.regxp.phone;
					this.validationMessage = this.$t('phone_invalid');
					break;
				case 'domain':
					this.validationTypeValidation = this.regxp.domain;
					this.validationMessage = this.$t('website_invalid');
					break;
				case 'time':
					this.validationTypeValidation = this.regxp.time;
					this.validationMessage = this.$t('time_invalid');
					break;
			}
		},
		isRegexValid(regexp) {
			if (!regexp) {
				regexp = this.validation;
			}

			const valueAsString = this.value.toString();
			const match = valueAsString.match(regexp);
			return match !== null && match[0] === valueAsString;
		},
		isValueValid(value) {
			if (!value) {
				value = this.model;
			}
			const valueAsString = value.toString();
			const validation = this.validation || this.validationTypeValidation;
			const match = valueAsString.match(validation);
			return match !== null && match[0] === valueAsString;
		},
		validate() {
			let isValid = true;
			if (!this.isDisabled) {
				const validation = this.validation || this.validationTypeValidation;
				if (isValid && this.required) {
					isValid = isValid && this.value != null && this.value.length !== 0;
				}

				if (typeof validation === 'function') {
					isValid = isValid && validation(this.value);
				} else if (isValid && validation && this.value) {
					const valueAsString = this.value.toString();
					const match = valueAsString.match(validation);

					isValid = isValid && match !== null && match[0] === valueAsString;
				}
			}

			if (this.minNumber && this.minNumber > this.value) {
				isValid = false;
			}

			this.isValid = isValid;
			this.$emit('validationComplete');
			return this.isValid;
		},
	},
};
</script>

<style scoped>
.base-input {
	@apply outline-none;
	height: 43px;
}

.form-group label {
	word-break: break-word;
}

.form-group.cursor-pointer label {
	@apply cursor-pointer;
}

.input-errors {
	@apply break-words;
}
</style>
